import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Modal, Button, message } from 'antd';
import { Canvas, PencilBrush } from 'fabric';
import './style.css';
import { workwiseServices } from '../../../../api';

const AddSignatureModal = ({ isVisible, onCancel, onSave, signatureData, documentId }) => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const [isCanvasEmpty, setIsCanvasEmpty] = useState(true);
  const [isCanvasInitialized, setIsCanvasInitialized] = useState(false);
  const [saving, setSaving] = useState(false);
  const [buttonClicked, setButtonClicked] = useState(false);

  // Memoize canvas to prevent rerenders
  const canvasContainer = useMemo(() => (
    <div className="canvas-container">
      <canvas ref={canvasRef} />
    </div>
  ), []);

  // Message strings
  const setting = {
    NoSignature: 'No signature provided',
    SignatureCreated: 'Signature saved successfully',
    ErrorSignature: 'Error saving signature',
    NoSignatureSave: 'No signature to save',
  };

  // Convert dataURL to Blob
  const getCanvasBlob = async (dataURL) => {
    const response = await fetch(dataURL);
    return await response.blob();
  };

  // Extract canvas data as JSON paths and PNG image
  const getDrawingData = async () => {
    if (fabricCanvasRef.current) {
      // Ensure all paths are rendered
      fabricCanvasRef.current.renderAll();
      const json = fabricCanvasRef.current.toJSON();
      console.log('Canvas JSON objects:', json.objects); // Debug
      const paths = json.objects
        .map((obj) => (obj.type === 'path' ? obj.path : null))
        .filter((path) => path !== null);
      console.log('Extracted paths:', paths); // Debug
      const pathsString = JSON.stringify(paths);
      const dataURL = fabricCanvasRef.current.toDataURL({ format: 'png' });
      const image = await getCanvasBlob(dataURL);
      return { json: pathsString, image };
    }
  };

  useEffect(() => {
    let isMounted = true;

    // Prevent interference from modal or parent events
    const preventInterference = (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log(`Event ${e.type} intercepted on canvas`);
    };

    const initializeCanvas = () => {
      if (!isVisible || !canvasRef.current || fabricCanvasRef.current) return;

      try {
        // Initialize Fabric.js canvas with white background
        fabricCanvasRef.current = new Canvas(canvasRef.current, {
          isDrawingMode: true,
          width: 400,
          height: 200,
          backgroundColor: '#fff'
        });

        // Set up pencil brush
        const brush = new PencilBrush(fabricCanvasRef.current);
        brush.color = '#000000';
        brush.width = 2;
        fabricCanvasRef.current.freeDrawingBrush = brush;

        // Ensure drawing mode is active
        fabricCanvasRef.current.isDrawingMode = true;

        // Track canvas changes
        const updateCanvasState = () => {
          const isEmpty = fabricCanvasRef.current.getObjects().length === 0;
          if (isMounted) {
            setIsCanvasEmpty(isEmpty);
            console.log('Canvas state:', isEmpty ? 'Empty' : `Contains ${fabricCanvasRef.current.getObjects().length} objects`);
          }
        };

        fabricCanvasRef.current.on('path:created', () => {
          console.log('Path created:', fabricCanvasRef.current.getObjects());
          fabricCanvasRef.current.renderAll();
          updateCanvasState();
        });
        fabricCanvasRef.current.on('object:removed', () => {
          console.log('Object removed:', fabricCanvasRef.current.getObjects());
          updateCanvasState();
        });
        fabricCanvasRef.current.on('before:render', () => {
          console.log('Before render:', fabricCanvasRef.current.getObjects());
        });
        fabricCanvasRef.current.on('mouse:down', () => {
          if (fabricCanvasRef.current.isDrawingMode && !fabricCanvasRef.current._isCurrentlyDrawing) {
            fabricCanvasRef.current._isCurrentlyDrawing = true; // Start drawing
            console.log('Mouse down: Started drawing, objects:', fabricCanvasRef.current.getObjects());
          }
        });
        fabricCanvasRef.current.on('mouse:move', () => {
          if (fabricCanvasRef.current._isCurrentlyDrawing) {
            console.log('Drawing in progress:', fabricCanvasRef.current.getObjects());
          }
        });
        fabricCanvasRef.current.on('mouse:up', () => {
          if (fabricCanvasRef.current.isDrawingMode && fabricCanvasRef.current._isCurrentlyDrawing) {
            fabricCanvasRef.current._isCurrentlyDrawing = false; // End drawing
            fabricCanvasRef.current.renderAll(); // Persist paths
            console.log('Mouse up: Finalized drawing, objects:', fabricCanvasRef.current.getObjects());
          }
        });

        // Prevent modal interference
        ['mousedown', 'mouseup', 'mousemove', 'mouseout', 'click', 'touchstart', 'touchmove', 'touchend'].forEach((event) => {
          canvasRef.current.addEventListener(event, preventInterference, { capture: true });
        });

        // Prevent text selection
        if (canvasRef.current.parentNode) {
          canvasRef.current.parentNode.style.userSelect = 'none';
        }

        if (isMounted) {
          setIsCanvasInitialized(true);
          console.log('Canvas initialized successfully');
        }
      } catch (error) {
        console.error('Failed to initialize canvas:', error);
        if (isMounted) {
          message.error('Failed to initialize signature canvas');
        }
      }
    };

    if (isVisible) {
      // Ensure DOM is ready
      const rafId = requestAnimationFrame(() => {
        if (canvasRef.current && isMounted && !fabricCanvasRef.current) {
          initializeCanvas();
        } else if (isMounted && !fabricCanvasRef.current) {
          const timeoutId = setTimeout(() => {
            if (canvasRef.current && isMounted && !fabricCanvasRef.current) {
              initializeCanvas();
            }
          }, 50);
          return () => clearTimeout(timeoutId);
        }
      });

      return () => cancelAnimationFrame(rafId);
    }

    return () => {
      isMounted = false;
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.off('path:created');
        fabricCanvasRef.current.off('object:removed');
        fabricCanvasRef.current.off('before:render');
        fabricCanvasRef.current.off('mouse:down');
        fabricCanvasRef.current.off('mouse:move');
        fabricCanvasRef.current.off('mouse:up');
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      if (canvasRef.current && canvasRef.current.parentNode) {
        canvasRef.current.parentNode.style.userSelect = '';
        ['mousedown', 'mouseup', 'mousemove', 'mouseout', 'click', 'touchstart', 'touchmove', 'touchend'].forEach((event) => {
          canvasRef.current.removeEventListener(event, preventInterference, { capture: true });
        });
      }
      if (isMounted) {
        setIsCanvasInitialized(false);
        setIsCanvasEmpty(true);
      }
      console.log('Canvas cleaned up');
    };
  }, [isVisible]);

  const clearCanvas = () => {
    if (fabricCanvasRef.current) {
      fabricCanvasRef.current.clear();
      setIsCanvasEmpty(true);
      console.log('Canvas cleared manually');
    }
  };

  const handleSave = async (e) => {
    if (saving) return;
    setButtonClicked(true);
    message.destroy();

    try {
      if (!fabricCanvasRef.current || fabricCanvasRef.current.getObjects().length === 0) {
        message.error(setting.NoSignature);
        return;
      }

      const { json, image } = await getDrawingData();
      if (!json || !image) {
        message.error(setting.NoSignatureSave);
        return;
      }

      const formData = new FormData();
      formData.append('attachment.file', image, 'signature.png');
      formData.append('signatureId', signatureData._id);
      formData.append('documentId', documentId);

      setSaving(true);
      let response;
      try {
        response = await workwiseServices.saveSignature(formData);
      } catch (apiError) {
        console.error('API error:', apiError);
        message.error(setting.ErrorSignature);
        return;
      }
      message.success(setting.SignatureCreated);
      clearCanvas();
      try {
        onSave({ e, signature: signatureData });
      } catch (saveError) {
        console.error('Error in onSave callback:', saveError);
      }

      onCancel();
    } finally {
      setSaving(false);
      setButtonClicked(false);
    }
  };

  return (
    <Modal
      title="Add Your Signature"
      visible={isVisible}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <Button
          className="btn-clear"
          onClick={clearCanvas}
          disabled={!isCanvasInitialized || saving}
        >
          Clear Signature
        </Button>,
        <Button
          className="btn-save"
          type="primary"
          onClick={handleSave}
          disabled={isCanvasEmpty || !isCanvasInitialized || saving}
          loading={saving}
        >
          Add Signature
        </Button>,
      ]}

      className="add-signature-modal"
    >
      {canvasContainer}
    </Modal>
  );
};

export default AddSignatureModal;